// Client Overview tab. Renders the client's summary + inline edit form
// (the content that used to live in the right-side <aside> before the
// Nav Part 2 refactor). Ports the saveMutation + optimistic update from
// the original ClientDetailPage.
import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { useIndustryOptions } from "@/lib/useIndustryOptions";
import { timeAgo } from "@/lib/utils";
import type {
  ClientDetailContext,
  ClientDetailResponse,
} from "@/pages/ClientDetailPage";
import { MentionTextarea } from "@/components/notes/MentionTextarea";
import { isLeadSource, leadSourceLabel, type LeadSource } from "@merchd/shared";
import { LeadSourceField, type LeadSourceValue } from "@/components/clients/LeadSourceField";
import { useAuth } from "@/auth/AuthProvider";
import { VendorCafeAccountCard } from "@/components/vendor-cafe/VendorCafeAccountCard";

const INDUSTRY_DATALIST_ID = "account-industry-suggestions";
const MONEY_CONTROLS_REASON = "Only admin or finance can change this.";

// Status enum mirrors apps/api/src/db/schema/enums.ts client_status.
const STATUS_OPTIONS = ["active", "prospect", "dormant", "closed"] as const;
type StatusOption = (typeof STATUS_OPTIONS)[number];

interface EditableFields {
  name: string;
  status: StatusOption;
  onCreditHold: boolean;
  creditHoldReason: string;
  notes: string;
  industry: string;
  taxExempt: boolean;
  taxExemptNote: string;
  /** The certificate file: its id + name, or null (Stephanie 2026-10-22). */
  certificate: { fileId: string; filename: string } | null;
  /** How they found us (CEO 2026-10-21); "" = unknown. */
  lead: LeadSourceValue;
}

interface PatchBody {
  name?: string;
  status?: StatusOption;
  on_credit_hold?: boolean;
  credit_hold_reason?: string | null;
  notes?: string | null;
  /** People newly tagged with "@" in the notes (Stephanie 2026-10-21). */
  mention_user_ids?: string[];
  industry?: string | null;
  tax_exempt?: boolean;
  tax_exempt_note?: string | null;
  tax_exempt_certificate_file_id?: string | null;
  lead_source?: string | null;
  lead_source_detail?: string | null;
}

function buildPatchBody(
  f: EditableFields,
  base: ClientDetailResponse,
): PatchBody {
  const body: PatchBody = {};
  if (f.name.trim() !== (base.name ?? "")) body.name = f.name.trim();
  if (f.status !== base.status) body.status = f.status;
  if (f.onCreditHold !== (base.onCreditHold === true)) {
    body.on_credit_hold = f.onCreditHold;
  }
  const newReason = f.onCreditHold ? f.creditHoldReason.trim() : "";
  if (newReason !== (base.creditHoldReason ?? "")) {
    body.credit_hold_reason = newReason.length > 0 ? newReason : null;
  }
  const newNotes = f.notes.trim();
  if (newNotes !== (base.notes ?? "")) {
    body.notes = newNotes.length > 0 ? newNotes : null;
  }
  const newIndustry = f.industry.trim();
  if (newIndustry !== (base.industry ?? "")) {
    body.industry = newIndustry.length > 0 ? newIndustry : null;
  }
  // An exempt account always sends its exemption (with the certificate
  // number, for the server's check), so pressing Save — even with nothing
  // else changed — clears tax off its open quotes (Stephanie 2026-11-02).
  if (f.taxExempt || f.taxExempt !== (base.taxExempt === true)) body.tax_exempt = f.taxExempt;
  const newCert = f.taxExempt ? f.taxExemptNote.trim() : "";
  if (f.taxExempt || newCert !== (base.taxExemptNote ?? "")) {
    body.tax_exempt_note = newCert.length > 0 ? newCert : null;
  }
  const newFile = f.taxExempt ? f.certificate?.fileId ?? null : null;
  if (base.taxExemptCertificateReady && newFile !== (base.taxExemptCertificate?.fileId ?? null)) {
    body.tax_exempt_certificate_file_id = newFile;
  }
  // Lead source: sent only when the field exists and something changed.
  if (base.leadSourceReady) {
    const src = f.lead.source || null;
    const detail = f.lead.source ? f.lead.detail.trim() || null : null;
    if (src !== (base.leadSource ?? null) || detail !== (base.leadSourceDetail ?? null)) {
      body.lead_source = src;
      body.lead_source_detail = detail;
    }
  }
  return body;
}

function validate(f: EditableFields): string | null {
  if (f.name.trim().length < 2) return "Name must be at least 2 characters.";
  if (f.onCreditHold && f.creditHoldReason.trim().length === 0) {
    return "Reason is required when credit hold is on.";
  }
  if (f.lead.source === "other" && !f.lead.detail.trim()) {
    return "Add a short note when the lead source is Other.";
  }
  return null;
}

export function ClientOverviewTab() {
  const { client: c, canEdit: canEditBase, clientId } =
    useOutletContext<ClientDetailContext>();
  const { user } = useAuth();
  // Credit hold + tax exemption are admin / finance only (security audit
  // 2026-10-21, H3 — the API refuses reps). Finance may open the edit form
  // to set them; reps see those two controls disabled with the reason.
  const canSetMoneyControls =
    !!user && (user.roles.includes("admin") || user.roles.includes("finance"));
  const canEdit = canEditBase || canSetMoneyControls;
  // Finance without admin / rep: money controls + notes only (review 2026-10-21).
  const financeOnly = canSetMoneyControls && !canEditBase && !(user?.roles.includes("admin") ?? false);
  const queryClient = useQueryClient();
  const detailQueryKey = ["clients", clientId, "detail"] as const;

  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<EditableFields | null>(null);
  const [noteMentions, setNoteMentions] = useState<string[]>([]);
  const industries = useIndustryOptions();
  // Exempt needs the certificate number OR the certificate file (Stephanie 2026-10-22).
  const exemptMissing = !!form?.taxExempt && !(form.taxExemptNote ?? "").trim() && !form.certificate;
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const uploadCertificate = async (file: File): Promise<void> => {
    setUploading(true);
    setUploadError(null);
    try {
      const presign = await api<{ file_id: string; upload_url: string; live: boolean }>("/files/presign", {
        method: "POST",
        body: JSON.stringify({
          filename: file.name,
          mime_type: file.type || "application/octet-stream",
          size_bytes: file.size,
          file_purpose: "client_provided",
          visibility: "internal",
          prefix: `clients/${clientId}/tax-exempt`,
        }),
      });
      if (presign.live) {
        const put = await fetch(presign.upload_url, { method: "PUT", headers: { "Content-Type": file.type || "application/octet-stream" }, body: file });
        if (!put.ok) throw new Error("upload failed");
      }
      setForm((f) => (f ? { ...f, certificate: { fileId: presign.file_id, filename: file.name } } : f));
    } catch {
      setUploadError("Couldn't upload that file. Try again, or type the certificate number.");
    } finally {
      setUploading(false);
    }
  };
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const enterEdit = (): void => {
    setForm({
      name: c.name ?? "",
      status: (STATUS_OPTIONS.find((s) => s === c.status) ??
        "prospect") as StatusOption,
      onCreditHold: c.onCreditHold === true,
      creditHoldReason: c.creditHoldReason ?? "",
      notes: c.notes ?? "",
      industry: c.industry ?? "",
      taxExempt: c.taxExempt === true,
      taxExemptNote: c.taxExemptNote ?? "",
      certificate: c.taxExemptCertificate ? { fileId: c.taxExemptCertificate.fileId, filename: c.taxExemptCertificate.filename } : null,
      lead: { source: isLeadSource(c.leadSource) ? (c.leadSource as LeadSource) : "", detail: c.leadSourceDetail ?? "" },
    });
    setValidationError(null);
    setSaveError(null);
    setIsEditing(true); setNoteMentions([]);
  };

  const cancelEdit = (): void => {
    setIsEditing(false);
    setForm(null);
    setValidationError(null);
    setSaveError(null);
  };

  // Esc cancels while editing.
  useEffect(() => {
    if (!isEditing) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") cancelEdit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isEditing]);

  const saveMutation = useMutation({
    mutationFn: async (body: PatchBody) =>
      api<{ client: ClientDetailResponse; retax?: { redone: number; skipped: number } }>(`/clients/${clientId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: detailQueryKey });
      const prior =
        queryClient.getQueryData<{ client: ClientDetailResponse }>(
          detailQueryKey,
        );
      if (prior?.client) {
        queryClient.setQueryData(detailQueryKey, {
          client: {
            ...prior.client,
            ...(body.name !== undefined ? { name: body.name } : {}),
            ...(body.status !== undefined ? { status: body.status } : {}),
            ...(body.on_credit_hold !== undefined
              ? { onCreditHold: body.on_credit_hold }
              : {}),
            ...(body.credit_hold_reason !== undefined
              ? { creditHoldReason: body.credit_hold_reason }
              : {}),
            ...(body.notes !== undefined ? { notes: body.notes } : {}),
            ...(body.industry !== undefined
              ? { industry: body.industry }
              : {}),
            ...(body.tax_exempt !== undefined ? { taxExempt: body.tax_exempt } : {}),
            ...(body.tax_exempt_note !== undefined ? { taxExemptNote: body.tax_exempt_note } : {}),
            ...(body.lead_source !== undefined ? { leadSource: body.lead_source, leadSourceDetail: body.lead_source_detail ?? null } : {}),
          },
        });
      }
      return { prior };
    },
    onError: (err, _body, context) => {
      if (context?.prior !== undefined) {
        queryClient.setQueryData(detailQueryKey, context.prior);
      }
      if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
        const body = err.body as { error?: string } | null;
        setSaveError(body?.error ?? "Save failed. Please review the form.");
      } else {
        setSaveError("Save failed. Try again or check your connection.");
      }
    },
    onSuccess: (data) => {
      // Keep anything the reply leaves out (e.g. the header's counts) and
      // re-read the account, so a partial reply can never blank the page.
      const prior = queryClient.getQueryData<{ client: ClientDetailResponse }>(detailQueryKey);
      queryClient.setQueryData(detailQueryKey, { client: { ...(prior?.client ?? {}), ...data.client } as ClientDetailResponse });
      void queryClient.invalidateQueries({ queryKey: detailQueryKey });
      // Tax exempt changed → the server redid open quotes' tax; show it fresh.
      if (data.retax) void queryClient.invalidateQueries({ queryKey: ["orders"] });
      setIsEditing(false);
      setForm(null);
      setValidationError(null);
      setSaveError(null);
    },
  });

  if (isEditing && form !== null) {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const err = validate(form);
          if (err !== null) {
            setValidationError(err);
            return;
          }
          setValidationError(null);
          setSaveError(null);
          const body = buildPatchBody(form, c);
          if (body.notes && noteMentions.length) body.mention_user_ids = noteMentions;
          if (Object.keys(body).length === 0) {
            cancelEdit();
            return;
          }
          saveMutation.mutate(body);
        }}
        className="grid gap-5 rounded-md border border-rule bg-white p-5 sm:max-w-xl sm:p-[22px]"
      >
        <div>
          <label
            htmlFor="client-name"
            className="block text-xs font-medium text-muted"
          >
            Name
          </label>
          <Input
            id="client-name"
            className="mt-1 text-lg font-semibold"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            disabled={saveMutation.isPending || financeOnly}
            autoFocus
            required
            minLength={2}
            maxLength={200}
          />
        </div>

        <div>
          <label
            htmlFor="client-status"
            className="block text-xs font-medium text-muted"
          >
            Status
          </label>
          <select
            id="client-status"
            className="mt-1 flex h-10 w-full rounded-md border border-brown-100 bg-white px-3 text-sm text-brown-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-peach-400 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50 disabled:cursor-not-allowed disabled:opacity-60"
            value={form.status}
            onChange={(e) =>
              setForm({ ...form, status: e.target.value as StatusOption })
            }
            disabled={saveMutation.isPending || financeOnly}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="flex min-h-[40px] items-center gap-2 text-sm font-medium text-ink">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-brown-200 text-rust-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-peach-400 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50"
              checked={form.onCreditHold}
              onChange={(e) =>
                setForm({ ...form, onCreditHold: e.target.checked })
              }
              disabled={saveMutation.isPending || !canSetMoneyControls}
            />
            On credit hold
          </label>
          {!canSetMoneyControls && (
            <p className="mt-1 text-xs text-brown-300">{MONEY_CONTROLS_REASON}</p>
          )}
          {form.onCreditHold && (
            <div className="mt-2">
              <label
                htmlFor="credit-hold-reason"
                className="block text-xs font-medium text-muted"
              >
                Hold reason
              </label>
              <Input
                id="credit-hold-reason"
                className="mt-1"
                value={form.creditHoldReason}
                onChange={(e) =>
                  setForm({ ...form, creditHoldReason: e.target.value })
                }
                disabled={saveMutation.isPending || !canSetMoneyControls}
                placeholder="Why is this account on hold?"
                required
                maxLength={500}
              />
            </div>
          )}
        </div>

        <div>
          <label
            htmlFor="client-industry"
            className="block text-xs font-medium text-muted"
          >
            Industry
          </label>
          <Input
            id="client-industry"
            list={INDUSTRY_DATALIST_ID}
            className="mt-1"
            value={form.industry}
            onChange={(e) => setForm({ ...form, industry: e.target.value })}
            disabled={saveMutation.isPending || financeOnly}
            placeholder="Pick one, or type a new industry"
            maxLength={120}
            autoComplete="off"
          />
          <datalist id={INDUSTRY_DATALIST_ID}>
            {industries.map((v) => (
              <option key={v} value={v} />
            ))}
          </datalist>
          <p className="mt-1 text-xs text-brown-300">
            Not in the list? Type it — it's saved and offered for the next account.
          </p>
        </div>

        {c.leadSourceReady && (
          <LeadSourceField
            id="client-lead-source"
            value={form.lead}
            onChange={(lead) => setForm({ ...form, lead })}
            disabled={saveMutation.isPending || financeOnly}
            required={false}
            allowCommonsku={c.leadSource === "repeat_from_commonsku"}
            labelClassName="block text-xs font-medium text-muted"
          />
        )}

        <div>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={form.taxExempt}
              onChange={(e) => setForm({ ...form, taxExempt: e.target.checked })}
              disabled={saveMutation.isPending || !canSetMoneyControls}
              className="h-4 w-4 rounded border-rule"
            />
            Tax exempt — no sales tax on this account's quotes
          </label>
          {!canSetMoneyControls && (
            <p className="mt-1 text-xs text-brown-300">{MONEY_CONTROLS_REASON}</p>
          )}
          {form.taxExempt && (
            <Input
              className="mt-2"
              value={form.taxExemptNote}
              onChange={(e) => setForm({ ...form, taxExemptNote: e.target.value })}
              disabled={saveMutation.isPending || !canSetMoneyControls}
              placeholder="Certificate number / expiry (if you have it)"
              maxLength={500}
              aria-label="Tax exemption certificate number"
              aria-invalid={exemptMissing}
            />
          )}
          {/* The certificate itself, if they have it (Stephanie 2026-10-22). */}
          {form.taxExempt && (
            c.taxExemptCertificateReady ? (
              <div className="mt-2 text-sm">
                {form.certificate ? (
                  <p className="flex flex-wrap items-center gap-2 text-ink">
                    <span>Certificate: <span className="font-medium">{form.certificate.filename}</span></span>
                    {c.taxExemptCertificate?.fileId === form.certificate.fileId && c.taxExemptCertificate.url && (
                      <a href={c.taxExemptCertificate.url} target="_blank" rel="noreferrer" className="text-xs font-semibold underline">View</a>
                    )}
                    <button type="button" className="text-xs font-semibold text-risk underline" disabled={saveMutation.isPending || !canSetMoneyControls} onClick={() => setForm({ ...form, certificate: null })}>Remove</button>
                  </p>
                ) : (
                  <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-semibold text-ink underline">
                    {uploading ? "Uploading…" : "Upload the certificate (PDF or picture)"}
                    <input
                      type="file"
                      accept="application/pdf,image/png,image/jpeg,image/webp,image/heic"
                      className="sr-only"
                      disabled={uploading || saveMutation.isPending || !canSetMoneyControls}
                      onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ""; if (file) void uploadCertificate(file); }}
                    />
                  </label>
                )}
                {uploadError && <p className="mt-1 text-xs font-semibold text-risk">{uploadError}</p>}
              </div>
            ) : (
              <p className="mt-1 text-xs text-brown-300">Uploading the certificate turns on after a database step from Jake. Type the number for now.</p>
            )
          )}
          {/* Number OR file once ticked (Stephanie 2026-10-16 / 2026-10-22). */}
          {exemptMissing && (
            <p className="mt-1 text-xs font-semibold text-risk">Add the certificate number or upload the certificate to save.</p>
          )}
          <p className="mt-1 text-xs text-brown-300">
            Saving redoes the tax on this account's quotes that aren't invoiced yet. Invoiced quotes keep their tax.
          </p>
        </div>

        <div>
          <label
            htmlFor="client-notes"
            className="block text-xs font-medium text-muted"
          >
            Notes
          </label>
          <MentionTextarea
            id="client-notes"
            className="mt-1 flex min-h-[7rem] w-full rounded-md border border-brown-100 bg-white px-3 py-2 text-sm text-brown-500 placeholder:text-[#8a8a85] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-peach-400 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50 disabled:cursor-not-allowed disabled:opacity-60"
            value={form.notes}
            onChange={(t, ids) => { setForm({ ...form, notes: t }); setNoteMentions(ids); }}
            placeholder="Type @ to tag someone"
            disabled={saveMutation.isPending}
            rows={4}
          />
        </div>

        {(validationError !== null || saveError !== null) && (
          <div className="rounded-md bg-risk-bg px-3 py-2 text-sm text-risk" role="alert">
            {validationError ?? saveError}
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <Button type="submit" disabled={saveMutation.isPending || exemptMissing || uploading}>
            {saveMutation.isPending ? "Saving…" : "Save changes"}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={cancelEdit}
            disabled={saveMutation.isPending}
          >
            Cancel
          </Button>
        </div>
      </form>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
      <section className="rounded-md border border-rule bg-white p-5 sm:p-[22px]">
        <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">Notes</h2>
        <p className="mt-0.5 text-xs text-muted">Internal — the client never sees these.</p>
        {c.notes ? (
          <p className="mt-3 whitespace-pre-wrap text-sm text-ink">{c.notes}</p>
        ) : (
          <p className="mt-3 text-sm text-muted">
            No notes yet.
            {canEdit && " Use Edit account to add context for the team."}
          </p>
        )}
        {c.creditHoldReason && c.onCreditHold && (
          <div className="mt-5 rounded-md bg-warn-bg px-3 py-2 text-sm text-warn">
            <strong className="font-medium">Credit hold:</strong>{" "}
            {c.creditHoldReason}
          </div>
        )}
      </section>

      <div className="space-y-6 lg:self-start">
        {/* Quick way into a new project — the main thing people do from an
            account (Stephanie 2026-11-02). Opens the Projects tab's form. */}
        <Link
          to={`/clients/${clientId}/projects?new=1`}
          className="flex h-10 w-full items-center justify-center gap-1.5 rounded-md bg-ink text-sm font-medium text-white hover:bg-ink-700"
        >
          + Project
        </Link>
        <aside className="rounded-md border border-rule bg-white p-5 sm:p-[22px]">
          <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">At a glance</h2>
          <dl className="mt-3 divide-y divide-[#e7e7e4] text-sm">
            <div className="flex items-baseline justify-between gap-3 py-2.5">
              <dt className="text-muted">Industry</dt>
              <dd className="text-right font-medium text-ink">
                {c.industry ? c.industry : <span className="font-normal text-muted">Not set</span>}
              </dd>
            </div>
            {c.leadSourceReady && (
              <div className="flex items-baseline justify-between gap-3 py-2.5">
                <dt className="text-muted">Found us via</dt>
                <dd
                  className="text-right font-medium text-ink"
                  title={[c.leadSourceDetail, c.leadSourceSetByName ? `Set by ${c.leadSourceSetByName}` : null].filter(Boolean).join(" · ") || undefined}
                >
                  {c.leadSource ? leadSourceLabel(c.leadSource) : <span className="font-normal text-muted">Unknown</span>}
                  {c.leadSourceDetail && <span className="block text-xs font-normal text-muted">{c.leadSourceDetail}</span>}
                </dd>
              </div>
            )}
            <div className="flex items-baseline justify-between gap-3 py-2.5">
              <dt className="text-muted">Sales tax</dt>
              <dd className="text-right font-medium text-ink" title={c.taxExemptNote ?? undefined}>
                {/* Tax exempt, out of state, or Florida — from the client's normal
                    address (Stephanie 2026-11-02). A Florida ship-to on a quote still gets tax. */}
                {c.taxExempt ? (
                  "Tax exempt"
                ) : c.taxLocation?.status === "out_of_state" ? (
                  <>Out of state ({c.taxLocation.state})<span className="block text-xs font-normal text-muted">No sales tax unless a quote ships to Florida</span></>
                ) : c.taxLocation?.status === "florida" ? (
                  <>Florida<span className="block text-xs font-normal text-muted">Sales tax charged</span></>
                ) : (
                  <span className="font-normal text-muted">Set by each quote's ship-to<span className="block text-xs">Add a default address to decide it</span></span>
                )}
                {c.taxExempt && c.taxExemptNote && <span className="block text-xs font-normal text-muted">Certificate #: {c.taxExemptNote}</span>}
                {c.taxExempt && c.taxExemptCertificate?.url && (
                  <a href={c.taxExemptCertificate.url} target="_blank" rel="noreferrer" className="block text-xs font-semibold underline">View certificate</a>
                )}
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-3 py-2.5">
              <dt className="text-muted">Contacts</dt>
              <dd className="font-medium tabular-nums text-ink">{c.contactCount}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-3 py-2.5">
              <dt className="text-muted">Last updated</dt>
              <dd className="font-medium text-ink">{timeAgo(c.updatedAt)}</dd>
            </div>
          </dl>
          {canEdit && (
            <Button onClick={enterEdit} variant="outline" className="mt-4 w-full">
              Edit account
            </Button>
          )}
        </aside>
        {/* Any staff member can set it (the server allows every staff role). */}
        <VendorCafeAccountCard clientId={clientId} canEdit />
      </div>
    </div>
  );
}
